import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, LogIn } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const navigate = useNavigate();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: 'Admin User', role: 'admin' }
          }
        });
        if (error) throw error;
        toast.success('Account created! Check email to verify.');
        setShowSignUp(false);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success('Welcome back!');
        navigate('/admin');
      }
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
    if (error) toast.error(error.message);
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({ 
      email,
      options: {
        emailRedirectTo: window.location.origin
      }
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Magic link sent! Check email.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-3xl shadow-2xl">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">
            VaultStack
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {showSignUp ? 'Create Account' : 'Small Business Platform'}
          </p>
        </div>

        {/* SSO Buttons - Primary */}
        <div className="space-y-3">
          <Button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full h-14 bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </Button>

          <Button
            onClick={handleMagicLink}
            disabled={loading}
            variant="outline"
            className="w-full h-14 border-2 border-slate-200 font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Email Magic Link
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-slate-500">Or</span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12"
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest" disabled={loading}>
            {loading ? 'Processing...' : 'Sign In'}
          </Button>
        </form>

        {/* Sign Up Toggle - Small & Discrete */}
        <div className="text-center">
          <button
            onClick={() => setShowSignUp(!showSignUp)}
            className="text-xs text-slate-400 hover:text-purple-600 underline underline-offset-4 font-bold"
          >
            {showSignUp ? 'Back to Sign In' : 'Need an account? Sign up'}
          </button>
        </div>
      </div>
    </div>
  );
};
